import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import Divider from '@mui/material/Divider'
import Form, { ButtonItem, SimpleItem, EmptyItem, GroupItem, Label, ColCountByScreen } from 'devextreme-react/form'
import JumboCardQuick from '@jumbo/components/JumboCardQuick'
import Typography from '@mui/material/Typography'
import CustomStore from 'devextreme/data/custom_store'
import { useSnackbar } from 'notistack'
import utilServices from 'app/services/util-services'
import customerServices from 'app/services/pages/reports/registrations/CustomerServices/customerServices'
import DataSource from 'devextreme/data/data_source'
import { PatternRule, RequiredRule } from 'devextreme-react/data-grid'
import { useSelector } from 'react-redux'
import { IconButton, Stack, useMediaQuery } from '@mui/material'
import WestIcon from '@mui/icons-material/West'
import GenderTypeService from 'app/utils/components/GenderType/GenderTypeService'

const AddTeacherForm = (props) => {
    const { enqueueSnackbar } = useSnackbar()
    const navigate = useNavigate()
    const [passwordMatch, setPasswordMatch] = useState(false)
    const [labelLocation, setLabelLocation] = useState('left')
    const [resetPasswordData, setResetPasswordData] = useState({
        password: '',
        verifyPassword: '',
    })
    const sm = useMediaQuery('(max-width:600px)')

    useEffect(() => {
        setLabelLocation(sm ? 'top' : 'left')
    }, [sm])

    const handlePasswordChange = useCallback(
        (e, fieldName) => {
            setResetPasswordData((prevData) => ({
                ...prevData,
                [fieldName]: e.value,
            }))

            if (fieldName === 'password') {
                setPasswordMatch(e.value === resetPasswordData.verifyPassword)
            } else if (fieldName === 'verifyPassword') {
                setPasswordMatch(e.value === resetPasswordData.password)
            }
        },
        [resetPasswordData]
    )

    const { handleSubmit } = useForm({
        resetOptions: {
            keepDirtyValues: true,
            keepErrors: true,
        },
    })
    const { loading } = useSelector((state) => state.teachers)

    function handleError(error) {
        if (error.status === '401') {
            navigate('/profile/signout')
        } else if (error.detail) {
            enqueueSnackbar(error.detail, {
                variant: 'error',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            })
        } else {
            enqueueSnackbar(error, {
                variant: 'error',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            })
        }
    }

    const submitButtonOptions = {
        text: 'Save',
        type: 'default',
        width: 120,
        useSubmitBehavior: true,
    }

    const cancelButtonOptions = {
        text: 'Cancel',
        type: 'Normal',
        width: 120,
        onClick: () => navigate('/security/teachers', { state: { navigateFrom: 'cancel-add-teacher' } }),
    }

    const validationRules = {
        firstName: [{ type: 'required', message: 'First Name is required.' }],
        lastName: [{ type: 'required', message: 'Last Name is required.' }],
        email: [{ type: 'required', message: 'Email is required.' }],
        password: [{ type: 'required', message: 'Password is required.' }],
        verifyPassword: [
            { type: 'required', message: 'Verify Password is required.' },
            {
                type: 'custom',
                validationCallback: () => passwordMatch,
                message: 'Password and Verify Password do not match.',
            },
        ],
    }

    const customerLookupList = useCallback(async (searchTerm) => {
        try {
            return await customerServices.getCustomerLookupList({ firstName: searchTerm })
        } catch (error) {
            handleError(error)
        }
    }, [])

    const customerLookupById = useCallback(async (key) => {
        try {
            return await customerServices.getCustomerLookupById(key)
        } catch (error) {
            handleError(error)
        }
    }, [])

    const customerLookupsStore = useMemo(() => {
        return new DataSource({
            store: new CustomStore({
                key: 'id',
                load: async function (loadOptions) {
                    const searchTerm = loadOptions.searchValue || ''
                    return await customerLookupList(searchTerm)
                },
                byKey: function (key) {
                    if (key === '') return utilServices.emptyList()
                    return customerLookupById(key)
                },
                errorHandler: function (e) {
                    console.error(e)
                },
            }),
            reshapeOnPush: true,
        })
    }, [])

    const customerEditorOptions = {
        dataSource: customerLookupsStore,
        valueExpr: 'id',
        searchEnabled: true,
        showClearButton: true,
        itemTemplate: function (data) {
            return data ? data.firstName.toFullName(data.lastName) : ''
        },
        displayExpr: function (data) {
            return data ? data.firstName.toFullName(data.lastName) : ''
        },
    }

    const phoneEditorOptions = {
        valueChangeEvent: 'keyup',
        maskRules: {
            X: /\d/,
        },
    }

    const phonePattern = /^\+[1-9]\d{10,14}$/

    return (
        <React.Fragment>
            <JumboCardQuick
                title={
                    <Stack direction='row' alignItems='center' spacing={1}>
                        <IconButton
                            className='backArrowForm'
                            onClick={() =>
                                navigate(`/security/teachers`, {
                                    state: { navigateFrom: 'cancel-add-teacher' },
                                })
                            }>
                            <WestIcon />
                        </IconButton>
                        <Typography color={'common.white'} variant={'h3'} sx={{ mt: 0.8 }}>
                            Add New Teacher
                        </Typography>
                    </Stack>
                }
                headerSx={{
                    height: 40,
                    bgcolor: 'primary.main',
                }}>
                <div>
                    <form onSubmit={handleSubmit(props.addTeacher)}>
                        <Form
                            mode='form'
                            formData={props.teacherData}
                            showColonAfterLabel={false}
                            showValidationSummary={false}
                            labelLocation={labelLocation}>
                            <GroupItem caption='New Teacher' cssClass='form-group-item '>
                                <ColCountByScreen lg={13} md={13} sm={1} xs={1} />
                                <GroupItem />
                                <GroupItem />
                                <GroupItem />
                                <GroupItem cssClass='form-group-item' colSpan={6}>
                                    <EmptyItem />
                                    <SimpleItem dataField='firstName' validationRules={validationRules.firstName}>
                                        <Label text='First Name' />
                                    </SimpleItem>
                                    <SimpleItem dataField='lastName' validationRules={validationRules.lastName}>
                                        <Label text='Last Name' />
                                    </SimpleItem>
                                    <SimpleItem dataField='phoneNumber' editorOptions={phoneEditorOptions}>
                                        <Label text='Phone Number (Optional)' />
                                        <PatternRule
                                            message='phone number must start with + and needs to include country code'
                                            pattern={phonePattern}
                                        />
                                    </SimpleItem>
                                    <SimpleItem dataField='email' validationRules={validationRules.email}>
                                        <Label text='Email' />
                                    </SimpleItem>
                                    <SimpleItem
                                        dataField='password'
                                        editorType='dxTextBox'
                                        validationRules={validationRules.password}
                                        editorOptions={{
                                            mode: 'password',
                                            value: resetPasswordData.password,
                                            onValueChanged: (e) => handlePasswordChange(e, 'password'),
                                            validationMessageMode: 'always',
                                        }}
                                    />
                                    <SimpleItem
                                        dataField='verifyPassword'
                                        editorType='dxTextBox'
                                        validationRules={validationRules.verifyPassword}
                                        editorOptions={{
                                            mode: 'password',
                                            value: resetPasswordData.verifyPassword,
                                            onValueChanged: (e) => handlePasswordChange(e, 'verifyPassword'),
                                            validationMessageMode: 'always',
                                        }}
                                    />
                                    <SimpleItem dataField='comments' editorType='dxTextArea'>
                                        <Label text='Comments' />
                                    </SimpleItem>
                                    <SimpleItem
                                        dataField='genderType'
                                        editorType='dxSelectBox'
                                        editorOptions={{
                                            dataSource: GenderTypeService.getGenderTypeForAccount(),
                                            displayExpr: 'genderType',
                                            valueExpr: 'value',
                                            searchEnabled: true,
                                            showClearButton: true,
                                        }}>
                                        <Label text='Gender Type' />
                                    </SimpleItem>
                                    <EmptyItem />
                                    <GroupItem>
                                        <Divider />
                                    </GroupItem>
                                    <EmptyItem />
                                    <SimpleItem
                                        dataField='customerId'
                                        editorType='dxSelectBox'
                                        editorOptions={customerEditorOptions}>
                                        <Label text='Customer Name' />
                                    </SimpleItem>
                                </GroupItem>
                            </GroupItem>
                            <EmptyItem />
                            <GroupItem>
                                <ColCountByScreen lg={2} md={2} sm={2} xs={2} />
                                <ButtonItem
                                    buttonOptions={{
                                        disabled: loading,
                                        icon: loading ? 'refresh' : 'check',
                                        ...submitButtonOptions,
                                    }}
                                    horizontalAlignment='right'
                                />
                                <ButtonItem buttonOptions={cancelButtonOptions} horizontalAlignment='left' />
                            </GroupItem>
                        </Form>
                    </form>
                </div>
            </JumboCardQuick>
        </React.Fragment>
    )
}

export default AddTeacherForm
